import { Button } from '@/components/ui/button';
import { pontosDe } from './fundacao-da-prancha';
import type { FormatoDaPeca, PecaLivreNaPrancha } from './use-peca-livre-na-prancha';

/**
 * O pilar e a viga com contorno sobre a prancha, e os controles da forma deles (06/10/2026).
 *
 * O traço em andamento é o polígono que o bloco também usa — linha tracejada andando até o
 * cursor, polígono cheio depois de fechado. O retângulo por dois cantos continua desenhado
 * pelo traçado da prancha, como sempre foi.
 */

/** O polígono ainda não cadastrado: aberto é linha até o cursor, fechado é contorno cheio. */
export function PecaEmTraco({
  peca,
  cor,
  zoom,
}: {
  peca: PecaLivreNaPrancha;
  /** A cor do pilar ou da viga, da legenda. */
  cor: string;
  zoom: number;
}) {
  const { traco } = peca;
  if (traco === undefined) return null;

  return (
    <g className="pointer-events-none">
      {traco.fechado && traco.vertices.length >= 3 ? (
        <polygon
          data-peca-nova=""
          points={pontosDe(traco.vertices)}
          fill={cor}
          fillOpacity={0.25}
          stroke={cor}
          strokeWidth={2 / zoom}
          strokeDasharray={`${4 / zoom} ${3 / zoom}`}
        />
      ) : (
        <polyline
          data-peca-nova=""
          points={pontosDe(traco.ao === undefined ? traco.vertices : [...traco.vertices, traco.ao])}
          fill="none"
          stroke={cor}
          strokeWidth={2 / zoom}
          strokeDasharray={`${4 / zoom} ${3 / zoom}`}
        />
      )}
      {traco.vertices.map((ponto, indice) => (
        <circle
          key={`pv-${String(indice)}`}
          cx={ponto.x}
          cy={ponto.y}
          r={(indice === 0 ? 6 : 4) / zoom}
          fill={cor}
        />
      ))}
    </g>
  );
}

const ROTULO_DA_FORMA: Readonly<Record<FormatoDaPeca, string>> = {
  RETANGULO: 'Retângulo',
  POLIGONO: 'Polígono',
};

/**
 * "Retângulo" e "Polígono", e o que só o polígono precisa: fechar o contorno e desfazer o
 * ponto. "Refazer" recomeça o traço nos dois — o retângulo mora no traçado da prancha, e
 * quem o limpa é o `onRefazer` de quem chamou.
 */
export function FormaDaPeca({
  peca,
  cantos,
  onEscolher,
  onRefazer,
}: {
  peca: PecaLivreNaPrancha;
  /** Quantos cantos do retângulo já foram clicados. */
  cantos: number;
  onEscolher: (formato: FormatoDaPeca) => void;
  onRefazer: () => void;
}) {
  const { formato } = peca;
  const aberto = formato === 'POLIGONO' && !peca.fechado;
  const marcados = formato === 'RETANGULO' ? cantos : peca.vertices;

  return (
    <div className="flex flex-col gap-2">
      <div role="group" aria-label="Forma da peça" className="flex flex-wrap gap-1">
        {(['RETANGULO', 'POLIGONO'] as const).map((item) => (
          <Button
            key={item}
            size="sm"
            variant={formato === item ? 'primary' : 'ghost'}
            aria-pressed={formato === item}
            onClick={() => onEscolher(item)}
          >
            {ROTULO_DA_FORMA[item]}
          </Button>
        ))}
      </div>
      <p className="text-xs text-(--color-content-muted)">
        {formato === 'RETANGULO'
          ? 'Arraste de um canto ao outro da peça — ou clique nos dois cantos, se preferir mirar. A seção sai do traço; as paredes em que ela cai, também.'
          : 'Clique os vértices do contorno e feche no primeiro ponto — ou em "Fechar contorno".'}
      </p>
      <div className="flex flex-wrap gap-2">
        {formato === 'POLIGONO' && (
          <>
            <Button
              variant="secondary"
              size="sm"
              disabled={!aberto || peca.vertices < 3}
              onClick={peca.fecharContorno}
            >
              Fechar contorno
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={peca.vertices === 0}
              onClick={peca.desfazerPonto}
            >
              Desfazer ponto
            </Button>
          </>
        )}
        <Button variant="ghost" size="sm" disabled={marcados === 0} onClick={onRefazer}>
          Refazer
        </Button>
      </div>
    </div>
  );
}
